import { onDayPartReorder } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo } from 'react';
import {
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';

import { useDayPartTree } from '../../hooks/use-day-part-tree';
import { GroupedListProps } from '../../types';
import * as Styled from './day-part-grouping-tasks-list.style';
import {
  DayPartRenderData,
  DayPartRenderDataContext,
} from './day-part-render-context';
import { DayPartRenderItem } from './day-part-render-item';

const taskItemTypes = [TaskListItemTypes.Task];

export const DayPartGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useDayPartTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onDayPartReorder({
      updatedItems,
      oldItems: Object.values(treeData),
      tasksMap,
      today,
      weekStartsOn,
    });

    onReorder(result.updatedSections, result.updatedTasks);
  };

  const renderData = useMemo<DayPartRenderData>(
    () => ({
      groupSettings,
      tasksMap,
      tasks,
      today,
      denseFormat,
      defaultNewTaskDate,
    }),
    [groupSettings, tasksMap, tasks, today, denseFormat, defaultNewTaskDate],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <DayPartRenderDataContext.Provider value={renderData}>
        <VirtualizedTreeList
          rootItemId={rootId}
          items={treeData}
          renderItem={DayPartRenderItem}
          scrollElement={scrollElement}
          multiselectTypes={taskItemTypes}
          dragTypes={taskItemTypes}
          onReorder={_onReorder}
          collapsedItems={collapsedSections}
          onCollapse={onSectionCollapse}
          denseFormat={denseFormat}
          isContextMenuOpen={isContextMenuOpen}
        />
      </DayPartRenderDataContext.Provider>
    </Styled.Container>
  );
};
